// User Settings — password change + PayPal email + user-owned LiveKit setup.
import { useState, useEffect } from "react";
import { Toaster, toast } from "sonner";
import { KeyRound, Shield, Mail, Download, Trash2, Lock } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import LiveKitSetupCard from "../components/LiveKitSetupCard";

export default function Settings() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);

  // PayPal email — required for NX bundle purchase, ad campaigns, boosts and withdrawals
  const [paypalEmail, setPaypalEmail] = useState("");
  const [paypalBusy, setPaypalBusy] = useState(false);

  useEffect(() => { if (user?.paypal_email) setPaypalEmail(user.paypal_email); }, [user?.paypal_email]);
  useEffect(() => { setProfilePrivate(Boolean(user?.profile_private)); }, [user?.profile_private]);

  const toggleProfilePrivate = async () => {
    setPrivateBusy(true);
    const next = !profilePrivate;
    try {
      await api.post("/me/privacy", { profile_private: next });
      setProfilePrivate(next);
      toast.success(next ? "Profile set to private" : "Profile set to public");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setPrivateBusy(false); }
  };

  const submit = async () => {
    if (!oldPw || !newPw) { toast.error("Enter current and new password"); return; }
    if (newPw.length < 8)  { toast.error("New password must be at least 8 characters"); return; }
    if (newPw !== confirmPw) { toast.error("New password confirmation does not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: oldPw, new_password: newPw });
      toast.success("Password updated. Please sign in again on other devices.");
      setOldPw(""); setNewPw(""); setConfirmPw("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  // Your data -- Privacy Policy §5 (access/export + deletion)
  const [exportBusy, setExportBusy] = useState(false);
  const [privateBusy, setPrivateBusy] = useState(false);
  const [profilePrivate, setProfilePrivate] = useState(false);
  const [delPw, setDelPw] = useState("");
  const [delConfirm, setDelConfirm] = useState("");
  const [delBusy, setDelBusy] = useState(false);

  const exportData = async () => {
    setExportBusy(true);
    try {
      const { data } = await api.get("/account/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `nexus-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally { setExportBusy(false); }
  };

  const deleteAccount = async () => {
    if (!delPw) { toast.error("Enter your current password"); return; }
    if (delConfirm !== "DELETE") { toast.error('Type DELETE (all caps) to confirm'); return; }
    setDelBusy(true);
    try {
      await api.post("/account/delete", { current_password: delPw, confirm: delConfirm });
      toast.success("Account deleted");
      logout();
      nav("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Deletion failed");
      setDelBusy(false);
    }
  };

  const savePaypal = async () => {
    const email = paypalEmail.trim().toLowerCase();
    if (!email || !email.includes("@") || !email.split("@")[1]?.includes(".")) {
      toast.error("Enter a valid PayPal email"); return;
    }
    setPaypalBusy(true);
    try {
      await api.post("/me/paypal-email", { paypal_email: email });
      toast.success("PayPal email saved. You can now buy NX, run ads and withdraw.");
      if (refresh) await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setPaypalBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="settings-page">
      <Toaster position="top-center" theme="dark" />
      <div className="space-y-2">
        <div className="label-eyebrow">// account settings</div>
        <h1 className="text-4xl font-black font-display tracking-tighter">Settings.</h1>
        {user && (
          <div className="text-sm text-white/60">
            Signed in as <span className="text-white font-medium">{user.display_name}</span>
            {user.email && <> · <span className="font-mono text-white/50">{user.email}</span></>}
          </div>
        )}
      </div>

      <section className="glass p-6 space-y-4" data-testid="change-password-card">
        <div>
          <div className="label-eyebrow"><KeyRound className="w-3 h-3 inline mr-1 mb-0.5" /> password</div>
          <h2 className="text-xl font-display font-bold mt-1">Change password</h2>
          <p className="text-xs text-white/50 mt-1">Minimum 8 characters. Store this somewhere safe — we can&apos;t recover it for you.</p>
        </div>
        <div className="space-y-3">
          <input
            type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)}
            placeholder="Current password"
            className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm"
            data-testid="cp-old"
          />
          <input
            type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
            placeholder="New password (8+ chars)"
            className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm"
            data-testid="cp-new"
          />
          <input
            type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
            placeholder="Confirm new password"
            className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm"
            data-testid="cp-confirm"
          />
          <button
            onClick={submit}
            disabled={busy}
            className="btn-nexus disabled:opacity-40"
            data-testid="cp-submit"
          >
            <Shield className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Update password"}
          </button>
          <Link to="/" className="text-[11px] text-white/40 hover:text-white/80 block">← back to feed</Link>
        </div>
      </section>

      <section className="glass p-6 space-y-4" data-testid="paypal-email-card">
        <div>
          <div className="label-eyebrow"><Mail className="w-3 h-3 inline mr-1 mb-0.5" /> paypal email</div>
          <h2 className="text-xl font-display font-bold mt-1">PayPal email</h2>
          <p className="text-xs text-white/50 mt-1">
            Required to buy NX bundles, create ad campaigns, boost your posts and withdraw your wallet to PayPal. Change this at any time — updates take effect immediately.
          </p>
          {user?.paypal_email ? (
            <div className="text-[11px] text-nexus-mint mt-2 font-mono">✓ On file · <span className="text-white/80">{user.paypal_email}</span></div>
          ) : (
            <div className="text-[11px] text-nexus-crimson mt-2">⚠ No PayPal email on file — add one below to unlock purchases and withdrawals.</div>
          )}
        </div>
        <div className="space-y-3">
          <input
            type="email" value={paypalEmail} onChange={(e) => setPaypalEmail(e.target.value)}
            placeholder="you@paypal.com"
            className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm"
            data-testid="paypal-email-input"
            autoCapitalize="none" autoCorrect="off"
          />
          <button
            onClick={savePaypal}
            disabled={paypalBusy}
            className="btn-nexus disabled:opacity-40"
            data-testid="paypal-email-save"
          >
            <Mail className="w-3.5 h-3.5" /> {paypalBusy ? "Saving…" : (user?.paypal_email ? "Update PayPal email" : "Save PayPal email")}
          </button>
        </div>
      </section>

      <section className="glass p-6 space-y-4" data-testid="profile-privacy-card">
        <div>
          <div className="label-eyebrow"><Lock className="w-3 h-3 inline mr-1 mb-0.5" /> privacy</div>
          <h2 className="text-xl font-display font-bold mt-1">Private profile</h2>
          <p className="text-xs text-white/50 mt-1">
            When on, your profile page is visible only to you and to Owner HQ — hidden from search and from everyone else, including people who already follow you.
          </p>
        </div>
        <button
          onClick={toggleProfilePrivate}
          disabled={privateBusy}
          className={`px-4 py-2.5 text-sm font-medium border transition-colors inline-flex items-center gap-2 disabled:opacity-40 ${profilePrivate ? "border-nexus-cyan text-nexus-cyan bg-nexus-cyan/10" : "border-white/15 text-white/70 hover:bg-white/5"}`}
          data-testid="toggle-profile-private"
        >
          <Lock className="w-3.5 h-3.5" /> {privateBusy ? "Saving…" : profilePrivate ? "Profile is private — tap to make public" : "Profile is public — tap to make private"}
        </button>
      </section>

      <section className="glass p-6 space-y-4" data-testid="your-data-card">
        <div>
          <div className="label-eyebrow"><Download className="w-3 h-3 inline mr-1 mb-0.5" /> your data</div>
          <h2 className="text-xl font-display font-bold mt-1">Export your data</h2>
          <p className="text-xs text-white/50 mt-1">
            Download everything tied to your account — profile, posts, streams, tips, purchases and follows — as a JSON file.
          </p>
        </div>
        <button
          onClick={exportData}
          disabled={exportBusy}
          className="btn-ghost disabled:opacity-40"
          data-testid="export-data-btn"
        >
          <Download className="w-3.5 h-3.5" /> {exportBusy ? "Preparing…" : "Download my data"}
        </button>
      </section>

      <section className="glass p-6 space-y-4 border border-nexus-crimson/30" data-testid="delete-account-card">
        <div>
          <div className="label-eyebrow text-nexus-crimson"><Trash2 className="w-3 h-3 inline mr-1 mb-0.5" /> danger zone</div>
          <h2 className="text-xl font-display font-bold mt-1">Delete account</h2>
          <p className="text-xs text-white/50 mt-1">
            Permanently removes your personal information (name, email, photos, contact details). This cannot be undone. Your username stops working immediately.
          </p>
        </div>
        <div className="space-y-3">
          <input
            type="password" value={delPw} onChange={(e) => setDelPw(e.target.value)}
            placeholder="Current password"
            className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm"
            data-testid="delete-account-password"
          />
          <input
            type="text" value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)}
            placeholder='Type DELETE to confirm'
            className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm"
            data-testid="delete-account-confirm"
          />
          <button
            onClick={deleteAccount}
            disabled={delBusy}
            className="px-4 py-2.5 text-sm font-medium border border-nexus-crimson text-nexus-crimson hover:bg-nexus-crimson/10 transition-colors inline-flex items-center gap-2 disabled:opacity-40"
            data-testid="delete-account-submit"
          >
            <Trash2 className="w-3.5 h-3.5" /> {delBusy ? "Deleting…" : "Permanently delete my account"}
          </button>
        </div>
      </section>

      <LiveKitSetupCard defaultOpen />
    </div>
  );
}
